import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import UniversityCard from './components/UniversityCard';
import FilterPanel from './components/FilterPanel';
import UniversityDetailModal from './components/UniversityDetailModal';
import ComparisonTool from './components/ComparisonTool';
import MyUniversitiesSection from './components/MyUniversitiesSection';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const UniversityDirectory = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    admitRate: 'all',
    gpa: 'all',
    sat: 'all',
    showFollowedOnly: false
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [followedUniversities, setFollowedUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [comparisonList, setComparisonList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeView, setActiveView] = useState('all');
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Fetch universities from Supabase
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await supabase
          .from('universities')
          .select('*');

        if (fetchError) throw fetchError;

        setUniversities(data || []);
      } catch (err) {
        console.error('Error fetching universities:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  // Load followed universities from profile or localStorage
  useEffect(() => {
    const loadFollowedUniversities = async () => {
      try {
        // Try to load from Supabase if user is authenticated
        if (user?.id) {
          const { data, error: fetchError } = await supabase
            .from('profiles')
            .select('followed_universities')
            .eq('id', user.id);

          if (fetchError) {
            console.error('Supabase profiles fetch error:', fetchError);
          }

          if (!fetchError && data && data.length > 0 && data[0]?.followed_universities) {
            setFollowedUniversities(data[0].followed_universities);
            // Sync to localStorage
            localStorage.setItem('followedUniversities', JSON.stringify(data[0].followed_universities));
            return;
          }
        }
        
        // Fallback to localStorage
        const stored = localStorage.getItem('followedUniversities');
        if (stored) {
          setFollowedUniversities(JSON.parse(stored));
        }
      } catch (err) {
        console.error('Error loading followed universities:', err);
        // Still try localStorage as fallback
        const stored = localStorage.getItem('followedUniversities');
        if (stored) {
          setFollowedUniversities(JSON.parse(stored));
        }
      }
    };

    loadFollowedUniversities();
  }, [user?.id]);

  // Save followed universities to profile and localStorage
  const saveFollowedUniversities = async (updatedList) => {
    try {
      // Always save to localStorage first (instant)
      localStorage.setItem('followedUniversities', JSON.stringify(updatedList));
      
      // Then save to Supabase if user is authenticated
      if (user?.id) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ followed_universities: updatedList })
          .eq('id', user.id);

        if (updateError) {
          console.error('Error saving to Supabase:', updateError);
          // Don't throw - localStorage already saved
        }
      }
    } catch (err) {
      console.error('Error saving followed universities:', err);
    }
  };

  const shuffleArray = (arr) => (arr || []).slice().sort(() => Math.random() - 0.5);


  const sortOptions = [
  { value: 'relevance', label: 'Most Relevant' },
  { value: 'acceptance-asc', label: 'Acceptance Rate (Low to High)' },
  { value: 'acceptance-desc', label: 'Acceptance Rate (High to Low)' },
  { value: 'tuition-asc', label: 'Tuition (Low to High)' },
  { value: 'tuition-desc', label: 'Tuition (High to Low)' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'name', label: 'Name (A-Z)' }];


  const filteredUniversities = useMemo(() => {
    let result = universities?.filter((uni) => {
      const matchesSearch = searchQuery === '' ||
      uni?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());

      const matchesFollowed = !filters?.showFollowedOnly || followedUniversities?.includes(uni?.id);

      const matchesAdmitRate = filters?.admitRate === 'all' || (() => {
        const rate = parseFloat(uni?.admit_rate);
        if (filters?.admitRate === 'very-selective') return rate < 10;
        if (filters?.admitRate === 'selective') return rate >= 10 && rate < 25;
        if (filters?.admitRate === 'moderate') return rate >= 25 && rate < 50;
        if (filters?.admitRate === 'less-selective') return rate >= 50;
        return true;
      })();

      const matchesGPA = filters?.gpa === 'all' || (() => {
        const gpaVal = parseFloat(uni?.gpa);
        if (filters?.gpa === 'high') return gpaVal >= 3.8;
        if (filters?.gpa === 'medium') return gpaVal >= 3.5 && gpaVal < 3.8;
        if (filters?.gpa === 'low') return gpaVal < 3.5;
        return true;
      })();

      const matchesSAT = filters?.sat === 'all' || (() => {
        if (!uni?.sat) return false;
        const satRange = uni?.sat.toLowerCase();
        if (filters?.sat === 'high') return satRange.includes('1500') || satRange.includes('1510') || satRange.includes('1520') || satRange.includes('1530') || satRange.includes('1540') || satRange.includes('1550') || satRange.includes('1560') || satRange.includes('1570') || satRange.includes('1580');
        if (filters?.sat === 'medium') return satRange.includes('1400') || satRange.includes('1410') || satRange.includes('1420') || satRange.includes('1430') || satRange.includes('1440') || satRange.includes('1450') || satRange.includes('1460') || satRange.includes('1470') || satRange.includes('1480') || satRange.includes('1490');
        if (filters?.sat === 'low') return satRange.includes('1200') || satRange.includes('1210') || satRange.includes('1220') || satRange.includes('1230') || satRange.includes('1240') || satRange.includes('1250') || satRange.includes('1260') || satRange.includes('1270') || satRange.includes('1280') || satRange.includes('1290') || satRange.includes('1300') || satRange.includes('1310') || satRange.includes('1320') || satRange.includes('1330') || satRange.includes('1340') || satRange.includes('1350') || satRange.includes('1360') || satRange.includes('1370') || satRange.includes('1380') || satRange.includes('1390');
        return true;
      })();

      return matchesSearch && matchesFollowed && matchesAdmitRate && matchesGPA && matchesSAT;
    });

    // Sorting options
    switch (sortBy) {
      case 'acceptance-asc':
        result?.sort((a, b) => (a?.admit_rate || 0) - (b?.admit_rate || 0));
        break;
      case 'acceptance-desc':
        result?.sort((a, b) => (b?.admit_rate || 0) - (a?.admit_rate || 0));
        break;
      case 'name':
        result?.sort((a, b) => (a?.name || '')?.localeCompare(b?.name || ''));
        break;
      case 'relevance':
      default:
        // Randomize default ordering
        result = shuffleArray(result);
        break;
    }

    return result;
  }, [universities, searchQuery, filters, sortBy]);

  // Reset to first page when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery]);

  const totalPages = Math.ceil((filteredUniversities?.length || 0) / pageSize);
  const currentPageUniversities = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredUniversities?.slice(start, end) || [];
  }, [filteredUniversities, currentPage]);

  const getVisiblePages = (current, total) => {
    // Always include first, last, and only previous/next one around current
    const set = new Set([1, total, current]);
    if (current - 1 >= 1) set.add(current - 1);
    if (current + 1 <= total) set.add(current + 1);
    const ordered = Array.from(set).sort((a, b) => a - b);

    const compact = [];
    for (let i = 0; i < ordered.length; i++) {
      const p = ordered[i];
      if (i === 0) {
        compact.push(p);
        continue;
      }
      const prev = ordered[i - 1];
      if (p - prev > 1) {
        compact.push('ellipsis');
      }
      compact.push(p);
    }
    return compact;
  };

  const followedUniversitiesList = universities?.filter((uni) =>
  followedUniversities?.includes(uni?.id)
  );

  const comparisonUniversities = universities?.filter((uni) =>
  comparisonList?.includes(uni?.id)
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      admitRate: 'all',
      gpa: 'all',
      sat: 'all',
      showFollowedOnly: false
    });
    setSearchQuery('');
  };

  const handleFollow = (id) => {
    const updatedList = followedUniversities?.includes(id) 
      ? followedUniversities?.filter((uniId) => uniId !== id) 
      : [...followedUniversities, id];
    setFollowedUniversities(updatedList);
    saveFollowedUniversities(updatedList);
  };

  const handleViewDetails = (university) => {
    setSelectedUniversity(university);
  };

  const handleAddToComparison = (id) => {
    if (comparisonList?.length < 4 && !comparisonList?.includes(id)) {
      setComparisonList((prev) => [...prev, id]);
    }
  };

  const handleRemoveFromComparison = (id) => {
    setComparisonList((prev) => prev?.filter((uniId) => uniId !== id));
  };

  const handleClearComparison = () => {
    setComparisonList([]);
  };

  return (
    <>
      <Helmet>
        <title>University Directory - UniSteps</title>
        <meta
          name="description"
          content="Use the UniSteps university directory to explore and compare institutions worldwide with rich data on programs, admissions, and campus life."
        />
      </Helmet>
      <div className="min-h-screen app-shell">
        <Header />

        <main className="pt-24 pb-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            <div className="surface-card rounded-2xl p-6 lg:p-8 overflow-hidden relative lifted">
              <div className="absolute inset-0 tile-grid opacity-60" aria-hidden="true" />
              <div className="absolute inset-0 aurora-blob opacity-45" aria-hidden="true" />
              <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <p className="text-sm uppercase tracking-[0.28em] text-muted-foreground">Global university atlas</p>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground headline-serif">Search, compare, and shortlist your best-fit campuses</h1>
                  <p className="text-muted-foreground max-w-3xl">Filter by region, admissions profile, and outcomes—built for careful academic decision-making.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <Input
                  placeholder="Search universities, programs, or locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  iconName="Search"
                  variant="filled"
                />
              </div>
              <div className="flex gap-3 flex-wrap">
                <Select
                  options={[
                    { value: 'all', label: 'All Admit Rates' },
                    { value: 'very-selective', label: 'Very Selective (<10%)' },
                    { value: 'selective', label: 'Selective (10-25%)' },
                    { value: 'moderate', label: 'Moderate (25-50%)' },
                    { value: 'less-selective', label: 'Less Selective (>50%)' }
                  ]}
                  value={filters.admitRate}
                  onChange={(value) => setFilters(prev => ({ ...prev, admitRate: value }))}
                  placeholder="Admit Rate"
                />
                <Select
                  options={[
                    { value: 'all', label: 'All GPAs' },
                    { value: 'high', label: 'High (3.8+)' },
                    { value: 'medium', label: 'Medium (3.5-3.8)' },
                    { value: 'low', label: 'Below 3.5' }
                  ]}
                  value={filters.gpa}
                  onChange={(value) => setFilters(prev => ({ ...prev, gpa: value }))}
                  placeholder="GPA Requirement"
                />
                <Select
                  options={[
                    { value: 'all', label: 'All SAT Scores' },
                    { value: 'high', label: 'High (1500+)' },
                    { value: 'medium', label: 'Medium (1400-1499)' },
                    { value: 'low', label: 'Below 1400' }
                  ]}
                  value={filters.sat}
                  onChange={(value) => setFilters(prev => ({ ...prev, sat: value }))}
                  placeholder="SAT Range"
                />
                <Button
                  variant={filters.showFollowedOnly ? 'primary' : 'outline'}
                  iconName="Heart"
                  onClick={() => setFilters(prev => ({ ...prev, showFollowedOnly: !prev.showFollowedOnly }))}
                >
                  {filters.showFollowedOnly ? 'Showing Liked' : 'Show Liked'} ({followedUniversities.length})
                </Button>
                {(filters.admitRate !== 'all' || filters.gpa !== 'all' || filters.sat !== 'all' || filters.showFollowedOnly) && (
                  <Button
                    variant="ghost"
                    iconName="X"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 text-center">
                <p>Error loading universities: {error}</p>
              </div>
            )}

            {!loading && !error && filteredUniversities?.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No universities found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? 'Try a different search term' : 'No universities available yet'}
                </p>
              </div>
            )}

            {!loading && !error && filteredUniversities?.length > 0 && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {currentPageUniversities?.map((university) => (
                    <UniversityCard
                      key={university?.id}
                      university={university}
                      onFollow={handleFollow}
                      isFollowing={followedUniversities?.includes(university?.id)}
                    />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 rounded-md text-sm border transition-smooth ${
                        currentPage === 1 ? 'bg-muted text-muted-foreground border-border cursor-not-allowed' : 'bg-card text-foreground border-border hover:bg-muted'
                      }`}
                      aria-label="Previous page"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>

                    {getVisiblePages(currentPage, totalPages).map((item, idx) => (
                      typeof item === 'number' ? (
                        <button
                          key={`p-${item}`}
                          onClick={() => setCurrentPage(item)}
                          className={`px-3 py-1.5 rounded-md text-sm border transition-smooth ${
                            currentPage === item
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-card text-foreground border-border hover:bg-muted'
                          }`}
                          aria-label={`Go to page ${item}`}
                        >
                          {item}
                        </button>
                      ) : (
                        <span
                          key={`e-${idx}`}
                          className="px-3 py-1.5 rounded-md text-sm text-muted-foreground border border-border select-none"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      )
                    ))}

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 rounded-md text-sm border transition-smooth ${
                        currentPage === totalPages ? 'bg-muted text-muted-foreground border-border cursor-not-allowed' : 'bg-card text-foreground border-border hover:bg-muted'
                      }`}
                      aria-label="Next page"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default UniversityDirectory;
